import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";

const SLUG = "lapsi-keittiossa-ikaopas";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Lapsi keittiössä — Ikäopas 1–5 v (Montessori 2026)";
const DESC =
  "Mitä 1-, 2-, 3-, 4- ja 5-vuotias osaa tehdä keittiössä turvallisesti — ikäkohtaiset tehtävät oppimistornin kanssa ja pedagoginen perustelu.";
const PUBLISHED = "2026-07-04";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/lapsi-keittiossa-ikaopas")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Ikäopas"
      title={TITLE}
      lead="Montessori-pedagogiikassa keittiö on lapsen paras luokkahuone: siellä yhdistyvät hienomotoriikka, matematiikka, kieli ja itsenäisyys. Tämä opas kertoo mihin ikävaihe kykenee — ja miten oppimistorni tuo lapsen työtasolle turvallisesti."
      readingMinutes={7}
      publishedISO={PUBLISHED}
    >
      <h2>Miksi keittiö on Montessorin ydintila</h2>
      <p>Maria Montessori kutsui käytännön arjen tehtäviä nimellä <em>practical life</em> — käytännön elämä. Ne eivät ole "puuhaa siksi että lapsi olisi vaiti", vaan strukturoituja harjoituksia, jotka rakentavat kolmea ydintaitoa:</p>
      <ul>
        <li><strong>Hienomotoriikka</strong> — pinsettiote, sekoittaminen, kaataminen. Sama motoriikka valmistaa käden kirjoittamiseen 4–5-vuotiaana.</li>
        <li><strong>Keskittyminen</strong> — 20 minuutin porkkanan raapimien on todistetusti kognitiivinen harjoitus. Lillard (2017) osoittaa, että Montessori-tehtäviin osallistuvilla lapsilla toiminnanohjaus on 12–18 kk verrokkiryhmää edellä.</li>
        <li><strong>Kausaalinen ajattelu</strong> — "kun sekoitan, taikina muuttuu; kun paistan, se kovettuu." Tämä on tieteellisen menetelmän alku.</li>
      </ul>
      <p>Keittiön ainoa fyysinen este on korkeus — työtaso on aikuisen mittainen. Oppimistorni ratkaisee tämän turvallisesti; sen vaihtoehtona toimii myös lattialla oleva lapsen työpöytä pienempiin tehtäviin.</p>

      <h2>Ikävuosikohtainen tehtävälista</h2>

      <h3>12–18 kk — ensimmäiset kosketukset</h3>
      <ul>
        <li>Vihannesten peseminen kylmässä vedessä (porkkana, peruna, salaatti).</li>
        <li>Voi levittää leivälle isolla lapsenpuukolla (tylsä).</li>
        <li>Pipari- tai pulla­taikinan sekoittaminen isolla lusikalla.</li>
        <li>Vedettävien laatikoiden avaaminen ja sulkeminen — hienomotoriikka.</li>
      </ul>
      <p>Aikuisen rooli: valmistelee kaiken lapsen ulottuville, näyttää liikkeen kerran hitaasti, väistyy sivuun.</p>

      <h3>18–24 kk — kaataminen ja siirtäminen</h3>
      <ul>
        <li>Veden kaataminen pienestä kannusta lasiin (aloita 100 ml, kasvata).</li>
        <li>Kuivien aineiden mittaus — jauhojen kaataminen kupista kulhoon.</li>
        <li>Munan rikkominen (tässä iässä yleensä 50 % onnistuu — se on ok).</li>
        <li>Kahvin tai teen tarjoilu vanhemmille — sosiaalinen taito.</li>
      </ul>

      <h3>2–3 v — leikkaaminen ja lämmin ruoka</h3>
      <ul>
        <li>Pehmeiden ruokien leikkaaminen lasten veitsellä: banaani, kurkku, keitetty peruna.</li>
        <li>Salaatin repiminen käsin.</li>
        <li>Munien vatkaaminen visualla.</li>
        <li>Voileivän kokonaan itse valmistaminen alusta loppuun.</li>
        <li>Astianpesukoneen täyttäminen (kuiviin lautasiin, aikuisen valvonnassa).</li>
      </ul>

      <h3>3–4 v — kuumat pinnat ja kokkaaminen</h3>
      <ul>
        <li>Munan paistaminen aikuisen rinnalla — lapsi kääntää spatulalla.</li>
        <li>Pannu­kakkutaikinan tekeminen alusta loppuun ohjatusti.</li>
        <li>Uunin avaaminen ja sulkeminen (patalappu, aikuisen valvonnassa).</li>
        <li>Vihannesten kuoriminen kuorimaveitsellä.</li>
        <li>Pöydän kattaminen kokonaan.</li>
      </ul>

      <h3>4–5 v — itsenäinen aamiainen ja välipala</h3>
      <ul>
        <li>Puuron valmistaminen mikrossa (mittaa, sekoittaa, kuumentaa).</li>
        <li>Salaatin tekeminen alusta loppuun — leikkaus, mausteet, kastike.</li>
        <li>Muffinssien paistaminen ohjeen mukaan.</li>
        <li>Ostoslistan lukeminen ja tavaroiden hakeminen kaapista.</li>
        <li>Yksinkertaisen aterian suunnittelu ja toteutus perheelle (esim. voileipäateria).</li>
      </ul>

      <h2>Käytännön järjestely — 5 vinkkiä</h2>
      <ol>
        <li><strong>Oma matala kaappi:</strong> yksi kaapin osa lapsen korkeudella — omat lasit, kulhot, ruokailuvälineet.</li>
        <li><strong>Vesipiste ulottuvilla:</strong> pieni vesikannuu ja lasi lapsen kaapissa. Ei tarvitse pyytää joka kerta.</li>
        <li><strong>Oppimistorni tiskialtaan viereen:</strong> pesu ja kuivaus onnistuvat ilman aikuisen nostoa.</li>
        <li><strong>Yksi työskentelymatto tai tabletti lattialle:</strong> selkeä työskentelyalue vähentää sotkun leviämistä.</li>
        <li><strong>Sotkun siivous osana tehtävää:</strong> sieni, pieni luuta ja rikkalapio lapsen korkeudella. Puhdistus ei ole rangaistus — se on osa työtä.</li>
      </ol>

      <h2>Yleisimmät kysymykset</h2>
      <h3>Eikö tämä kestä liian kauan?</h3>
      <p>Kyllä, ensimmäiset kuukaudet. 3-vuotias voileivän valmistaminen kestää 15 min, vanhemmalta 30 sekuntia. Investointi kannattaa: 5-vuotiaana lapsi tekee aamupalan itse, ja päivittäinen aika taittuu vanhempien puolelta lähes nollaan.</p>
      <h3>Turvallisuus — kuumat pinnat?</h3>
      <p>Alle 3-vuotiaan pito etukauimpana keittolevystä. Kääntele kattilan kädensijat sisäänpäin. Kaikki terävät veitset lukittuun laatikkoon. Aloita lapsen omalla, tylsällä lastenveitsellä.</p>

      <h2>Yhteenveto</h2>
      <p>Keittiön ei tarvitse olla lapsen "leikkihuone" — se saa säilyä keittiönä. Muutos on pieni: oppimistorni tiskialtaan viereen, yksi matala kaappi lapsen astioille, ja aikuinen valmis odottamaan 5 sekuntia ennen kuin auttaa. Loput kasvaa itsestään lapsen mukana.</p>
      <p>
        Lue lisää oppimistornista:{" "}
        <Link to="/oppimistorni">Oppimistornit lapselle →</Link> ·{" "}
        <Link to="/montessori-koulu/oppimistorni-turvallisuus">Turvallisuustarkistuslista →</Link>
      </p>
    </BlogPost>
  );
}